import React, { createContext, useState, useEffect, useContext } from 'react';
import commonApi from '../utils/api';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';

export const MerchantWarehouseContext = createContext();

const initialFilters = {
    merchant_id: [],
    status: '',
    dateRange: { startDate: null, endDate: null },
};

export function MerchantWarehouseContextProvider({ children }) {
    const { user, setModalAlert, setOpenAlert, setAlertMessage, setSeverity } = useContext(CommonContext);

    const [addModal, setAddModal] = useState(false);
    const [allData, setAllData] = useState([]);
    const [allAssignments, setAllAssignments] = useState([]);
    const [viewData, setViewData] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [warehouseInfo, setWarehouseInfo] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    const handleAddModal = () => {
        setAddModal(true);
        setModalAlert(false);
        setOpenAlert(false);
        setAlertMessage('');
        setSeverity('');
    };

    const handleCloseModal = () => {
        setViewData('')
        setIsEdit(false);
        setAddModal(false);
    };

    const onLoadApi = (customFilters = filters) => {
        if (!location?.state?.warehouse_master_id) {
            console.error('warehouse_master_id is required');
            return;
        }

        let payload = {
            ...customFilters,
            warehouse_master_id: location.state.warehouse_master_id,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        delete payload.dateRange;

        setAllAssignments([]);
        setAllData([]);
        setFirstLoading(false);
        commonApi.post(`/warehouse-master-merchant/list?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setFirstLoading(true);
                if (response?.data?.s === 1) {
                    setAllAssignments(response?.data?.data || []);
                    setAllData(response?.data);
                    if (response?.data?.data?.length > 0) {
                        setWarehouseInfo(response.data.data[0].warehouse_master);
                    }
                }
            })
            .catch((error) => {
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const viewDetail = (id) => {
        setIsLoading(true);
        commonApi.post('/warehouse-master-merchant/view', { id: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    setIsEdit(true);
                    setViewData(response?.data?.data);
                    handleAddModal();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    }

    const toggleStatus = (id, status) => {
        setIsLoading(true);
        commonApi.post('/warehouse-master-merchant/status', { id: id, status: status })
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 1) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('success');
                    onLoadApi();
                } else {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setOpenAlert(true);
                setAlertMessage(error?.message || 'Something went wrong');
                setSeverity('error')
            });
    }

    useEffect(() => {
        if (location?.state?.warehouse_master_id) {
            onLoadApi();
        } else {
            navigate('/master-warehouse');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, location.state]);

    const contextValue = {
        warehouseInfo,
        filters,
        setFilters,
        initialFilters,
        viewDetail,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        isEdit,
        onLoadApi,
        isLoading,
        firstLoading,
        viewData,
        allAssignments,
        allData,
        handleAddModal,
        handleCloseModal,
        toggleStatus,
        addModal,
        warehouseMasterId: location?.state?.warehouse_master_id
    };

    return <MerchantWarehouseContext.Provider value={contextValue}>{children}</MerchantWarehouseContext.Provider>;
}
